import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, Sparkles, Layers, Landmark, TrendingUp, Wrench, BookOpen, ShieldCheck, CheckCircle2 } from "lucide-react";
import { PageShell, Ticker, Eyebrow } from "../components/Layout";
import Gauge from "../components/Gauge";
import { STATS, CURATED_INDEX, WORKFLOWS, DESTINATIONS, REGULATORS, CORRIDORS, MONEY_METRICS, ADVISORS, GLOSSARY } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Layers, Landmark, TrendingUp, Wrench, BookOpen, ShieldCheck };

const NewsletterForm = ({ compact }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email, role: role || null });
      setDone(true);
      toast({ title: "Subscribed", description: "You're on the Monday briefing list. Welcome aboard." });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast({ title: "Could not subscribe", description: msg });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-3 text-white" data-testid="newsletter-success">
        <CheckCircle2 className="text-emerald-400" size={22} />
        <p className="text-sm text-white/90">Subscribed — see you Monday.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 w-full" data-testid="newsletter-form">
      <input
        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="your.email@bank.com.au" data-testid="newsletter-email-input"
        className="w-full bg-white text-ink rounded-md px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent placeholder:text-neutral-400"
      />
      <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="newsletter-role-select"
        className="w-full bg-white text-ink rounded-md px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-accent">
        <option value="">Your role (optional)</option>
        <option>Risk Manager / CRO</option>
        <option>GRC Consultant</option>
        <option>Internal Auditor</option>
        <option>Sales / BD</option>
        <option>Other</option>
      </select>
      <button type="submit" disabled={loading} data-testid="newsletter-submit-btn"
        className="bg-accent text-white rounded-md px-6 py-3 text-sm font-semibold hover:bg-accent-dark transition-colors duration-200 disabled:opacity-60">
        {loading ? "Subscribing…" : "Subscribe free"}
      </button>
    </form>
  );
};

export { NewsletterForm };

const Home = () => {
  const [cashRate, setCashRate] = useState(null);

  useEffect(() => {
    axios.get(`${API}/market/cash-rate`).then((r) => setCashRate(r.data)).catch(() => {});
  }, []);

  return (
  <PageShell>
    {/* HERO */}
    <section className="relative overflow-hidden hero-grid">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border-b-2 border-accent/30 pb-1 mb-6">
            <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
            <span className="font-mono text-[11px] font-semibold tracking-[0.25em] uppercase text-accent">Australia Edition · Jul 2025</span>
          </div>
          <h1 className="font-heading text-[44px] sm:text-[62px] lg:text-[74px] font-bold leading-[1.02] tracking-tight text-ink">
            Match the right<br />risk framework.<br /><span className="text-accent">For Australia.</span>
          </h1>
          <p className="mt-7 text-[17px] leading-relaxed text-ink/65 max-w-xl">
            The Risk Encyclopedia, localised. CPS 234, CPS 230, Privacy Act, Essential Eight — translated into operating playbooks for CROs, internal auditors and GRC consultants across Australian BFSI.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/tools/cars-calculator" data-testid="hero-primary-cta" className="inline-flex items-center gap-2 bg-accent text-white px-6 py-3.5 rounded-md text-[14px] font-semibold hover:bg-accent-dark hover:-translate-y-0.5 transition-[background-color,transform] duration-200 shadow-[0_8px_24px_rgba(225,29,46,0.25)]">
              <Sparkles size={16} /> 5-question framework matcher
            </Link>
            <Link to="/frameworks" data-testid="hero-secondary-cta" className="inline-flex items-center gap-2 border border-neutral-300 text-ink px-6 py-3.5 rounded-md text-[14px] font-semibold hover:border-ink transition-colors duration-200">
              Browse the library <ArrowRight size={16} />
            </Link>
          </div>
          <div className="mt-12">
            <p className="font-mono text-[10.5px] font-semibold tracking-[0.3em] uppercase text-neutral-400 mb-4">A curated index</p>
            <div className="flex flex-wrap gap-2 max-w-xl">
              {CURATED_INDEX.map((f) => (
                <Link key={f} to="/frameworks" className="font-mono text-[11.5px] border border-neutral-300 text-ink/70 px-2.5 py-1 rounded-sm hover:border-accent hover:text-accent transition-colors duration-150">{f}</Link>
              ))}
            </div>
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-col gap-5">
          <div className="bg-white border border-neutral-200 rounded-lg p-7 shadow-[0_20px_60px_rgba(15,27,45,0.07)]">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-mono text-[10.5px] font-semibold tracking-[0.3em] uppercase text-accent border-t-2 border-accent pt-2 inline-block">Flagship metric</p>
                <h3 className="font-heading text-lg font-bold text-ink mt-2">Composite Australia Risk Score</h3>
              </div>
              <span className="font-mono text-[10px] text-neutral-400 tracking-wider mt-1">CARS · v1.0</span>
            </div>
            <Gauge value={74} />
            <p className="text-[12.5px] text-ink/55 leading-relaxed mt-2">
              Combines CPS 234, CPS 230, Essential Eight, privacy, third-party risk and operational resilience into one 0–100 metric.
            </p>
            <Link to="/tools/cars-calculator" data-testid="gauge-cta" className="inline-flex items-center gap-1.5 text-accent text-[13px] font-semibold mt-4 hover:gap-2.5 transition-[gap] duration-200">
              Score my organisation <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 border border-neutral-200 rounded-lg overflow-hidden bg-white">
            {STATS.map((s, i) => (
              <div key={s.label} className={`p-5 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} border-neutral-200`}>
                <p className="font-heading text-3xl font-bold text-accent">{s.value}</p>
                <p className="text-[12.5px] text-ink/55 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <Ticker />

    {/* WORKFLOWS */}
    <section className="py-20 lg:py-28 bg-paper">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        <Eyebrow>Automation · save · share</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <h2 className="font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.05]">12 pre-built workflows. Run in minutes, not weeks.</h2>
            <p className="mt-5 text-[15px] text-ink/60 leading-relaxed">Pick one, save it to your profile, schedule it. Each run produces an executive brief you can share with the board.</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/tools" className="inline-flex items-center gap-2 bg-ink text-white px-5 py-3 rounded-md text-[13.5px] font-semibold hover:bg-ink/85 transition-colors duration-200">Browse 12 workflows</Link>
              <Link to="/insights" className="inline-flex items-center gap-2 border border-neutral-300 text-ink px-5 py-3 rounded-md text-[13.5px] font-semibold hover:border-ink transition-colors duration-200">Read the weekly briefing</Link>
            </div>
          </div>
          <div className="lg:col-span-7 flex flex-wrap gap-2.5 content-start">
            {WORKFLOWS.map((w) => (
              <span key={w} className="font-mono text-[12px] bg-white border border-neutral-200 text-ink/70 px-3.5 py-2 rounded-md hover:border-accent hover:text-accent transition-colors duration-150 cursor-default">{w}</span>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* DESTINATIONS */}
    <section className="py-20 lg:py-28">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        <Eyebrow>One platform · six destinations</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <h2 className="lg:col-span-7 font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.05]">Group by what you actually need to do.</h2>
          <p className="lg:col-span-5 text-[15px] text-ink/60 leading-relaxed">The top menu groups every page into six clean destinations. Pick your path; we'll keep you out of the weeds.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-neutral-200">
          {DESTINATIONS.map((d) => {
            const Icon = ICONS[d.icon];
            return (
              <Link key={d.title} to={d.to} data-testid={`destination-${d.title.toLowerCase().replace(/\s/g, "-")}`} className="group border-r border-b border-neutral-200 p-8 hover:bg-paper transition-colors duration-200">
                <Icon size={26} className="text-ink group-hover:text-accent transition-colors duration-200" strokeWidth={1.6} />
                <h3 className="font-heading text-[22px] font-bold text-ink mt-5">{d.title}</h3>
                <p className="text-[13.5px] text-ink/55 leading-relaxed mt-2 min-h-[42px]">{d.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-accent text-[13px] font-semibold mt-5 group-hover:gap-2.5 transition-[gap] duration-200">
                  {d.cta} <ArrowUpRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>

    {/* AUSTRALIA HUB */}
    <section className="py-20 lg:py-28 bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 relative">
        <Eyebrow light>Australia Regulatory Hub</Eyebrow>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <h2 className="lg:col-span-7 font-heading text-4xl lg:text-[46px] font-bold tracking-tight leading-[1.05]">Eight regulators. One operating manual.</h2>
          <div className="lg:col-span-5">
            <p className="text-[15px] text-white/60 leading-relaxed mb-5">Deep-dive control catalogues, applicability matrices, compliance calendars and penalty frameworks for APRA, OAIC, AUSTRAC and every Australian BFSI rulebook.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/australia" data-testid="hub-explore-cta" className="inline-flex items-center gap-2 bg-accent text-white px-5 py-3 rounded-md text-[13.5px] font-semibold hover:bg-accent-dark transition-colors duration-200">Explore Australia Hub</Link>
              <Link to="/australia/home-affairs" className="inline-flex items-center gap-2 border border-white/25 text-white px-5 py-3 rounded-md text-[13.5px] font-semibold hover:border-white transition-colors duration-200">12-hour breach playbook</Link>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {REGULATORS.map((r) => (
            <Link key={r.slug} to={`/australia/${r.slug}`} data-testid={`regulator-card-${r.slug}`} className="group bg-white/[0.04] border border-white/10 rounded-lg p-5 hover:bg-white/[0.08] hover:border-accent/50 transition-colors duration-200">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent-light">{r.tag}</span>
              <h3 className="font-heading text-lg font-bold mt-2">{r.name}</h3>
              <p className="font-mono text-[11px] text-white/45 mt-1.5 leading-relaxed">{r.detail}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* MONEY FLOW */}
    <section className="py-20 lg:py-28 bg-paper">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        <Eyebrow>Money Flow Intelligence</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <h2 className="font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.05]">Live signals — straight from the corridor.</h2>
          <Link to="/money-flow" data-testid="moneyflow-cta" className="inline-flex items-center gap-1.5 text-accent text-[14px] font-semibold hover:gap-2.5 transition-[gap] duration-200">Open Money Flow Hub <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-neutral-200 rounded-lg p-7">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400">{MONEY_METRICS[0].label}</p>
              <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-sm border ${cashRate?.live ? "text-emerald-600 border-emerald-200 bg-emerald-50" : "text-neutral-400 border-neutral-200"}`}>
                {cashRate?.live ? "Live · RBA" : "Indicative"}
              </span>
            </div>
            <p className="font-heading text-6xl font-bold text-ink mt-5" data-testid="home-cash-rate">{cashRate ? `${cashRate.rate.toFixed(2)}%` : MONEY_METRICS[0].value}</p>
            <p className="font-mono text-[12px] text-emerald-600 mt-2">{cashRate?.date ? `As at ${cashRate.date}` : MONEY_METRICS[0].sub}</p>
            <p className="text-[13px] text-ink/55 leading-relaxed mt-4">{MONEY_METRICS[0].note}</p>
          </div>
          <div className="bg-white border border-neutral-200 rounded-lg p-7">
            <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-neutral-400 mb-5">Top corridors · AUD mn</p>
            <div className="space-y-4">
              {CORRIDORS.map((c) => (
                <div key={c.name} className="flex items-center justify-between gap-3 border-b border-neutral-100 pb-3 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 animate-pulse" />
                    <span className="text-[13.5px] text-ink/80 truncate">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="font-heading text-[15px] font-bold text-ink">{c.vol}</span>
                    <span className="font-mono text-[11px] text-emerald-600 w-[86px] text-right">{c.yoy}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* AI ADVISORS */}
    <section className="py-20 lg:py-28">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <Eyebrow>AI Advisor · Australia-aware</Eyebrow>
          <h2 className="font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.05]">Four advisors. One panel. Australia-aware answers.</h2>
          <p className="mt-5 text-[15px] text-ink/60 leading-relaxed max-w-lg">
            Switch between <strong>Matilda</strong> (general GRC), <strong>Arjun</strong> (Privacy / OAIC), <strong>Sophie</strong> (Money Flow) and <strong>Jack</strong> (APRA Regs). Multi-turn, persona-aware, with disclaimers built in.
          </p>
          <Link to="/advisor" data-testid="open-advisor-cta" className="inline-flex items-center gap-2 bg-ink text-white px-6 py-3.5 rounded-md text-[14px] font-semibold mt-8 hover:bg-ink/85 transition-colors duration-200">
            <Sparkles size={16} /> Open AI Advisor
          </Link>
        </div>
        <div className="lg:col-span-6 grid grid-cols-2 gap-4">
          {ADVISORS.map((a) => (
            <Link to="/advisor" key={a.name} className="border border-neutral-200 rounded-lg p-6 flex flex-col items-center text-center hover:border-accent/40 hover:shadow-[0_12px_32px_rgba(15,27,45,0.07)] transition-[border-color,box-shadow] duration-200">
              <span className="w-12 h-12 rounded-full bg-ink text-white font-heading font-bold text-lg flex items-center justify-center">{a.initial}</span>
              <p className="font-heading font-bold text-ink mt-4">{a.name}</p>
              <p className="font-mono text-[11px] text-neutral-400 mt-1 tracking-wide">{a.role}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* GLOSSARY PREVIEW */}
    <section className="py-20 lg:py-28 bg-paper">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        <Eyebrow>Live · updated from trending searches</Eyebrow>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <h2 className="font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.05]">The Risk Glossary</h2>
          <Link to="/glossary" data-testid="glossary-cta" className="inline-flex items-center gap-1.5 text-accent text-[14px] font-semibold hover:gap-2.5 transition-[gap] duration-200">Browse all terms <ArrowRight size={15} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GLOSSARY.slice(0, 6).map((g) => (
            <Link key={g.slug} to="/glossary" className="group bg-white border border-neutral-200 rounded-lg p-6 hover:border-accent/40 hover:-translate-y-1 transition-[border-color,transform] duration-200">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{g.cat}</span>
              <h3 className="font-heading text-[17px] font-bold text-ink mt-2.5">{g.term}</h3>
              <p className="text-[13px] text-ink/55 leading-relaxed mt-2 line-clamp-3">{g.def}</p>
              <span className="inline-flex items-center gap-1 text-accent text-[12.5px] font-semibold mt-4">Detail <ArrowUpRight size={13} /></span>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* NEWSLETTER */}
    <section className="py-20 lg:py-28 bg-ink text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid-dark pointer-events-none" />
      <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
        <div>
          <Eyebrow light>Australia Risk Briefing</Eyebrow>
          <h2 className="font-heading text-4xl lg:text-[42px] font-bold tracking-tight leading-[1.08]">Every Monday. Free. Unsubscribe anytime.</h2>
          <p className="mt-5 text-[15px] text-white/60 leading-relaxed">A 4-minute read on the week ahead — APRA, OAIC, AUSTRAC and ACSC developments distilled for AU BFSI professionals.</p>
        </div>
        <div className="bg-white/[0.05] border border-white/10 rounded-lg p-7">
          <NewsletterForm />
        </div>
      </div>
    </section>
  </PageShell>
  );
};

export default Home;
